/**
 * The character this tab is playing, and the writes that keep it.
 *
 * The shell wants a name and a way to change it, not a store, a migration chain and an async
 * failure to handle at the moment somebody presses the dice. So this holds the profile, writes on
 * every change, and keeps the last failure where a screen can ask for it.
 *
 * Writes are fire and forget on purpose. Every save point that exists today is a menu interaction,
 * and blocking one on a disk to show a spinner nobody reads would be worse than the thing it
 * guards against. A failed write leaves the in-memory profile as it was on screen and the failure
 * readable, which is the honest state: the player has the name they chose, and it is not saved yet.
 *
 * `boot` is the whole start-up path in one call, fallbacks included, so `main.ts` never has to
 * decide what to do about a browser with no storage.
 *
 * `adopt` is the one write here that is awaited. Importing a file is a transaction the player is
 * watching, and it is the only one where saying "done" before the disk agreed would cost the thing
 * they pressed the button to protect.
 */

import { openSaveStorage, resolveActiveProfile } from '@/save/activeProfile';
import { refused } from '@/save/saveResult';
import { newProfile } from '@/save/saveContract';
import { rollPlayerName } from '@/identity/rollPlayerName';
import type { ProfileStore, ProfileStoreOptions } from '@/save/profileStore';
import type { SaveProfile } from '@/save/saveContract';
import type { SaveFailure, SaveResult } from '@/save/saveResult';

/** What adopting an imported profile would do to what is already stored. */
export interface IncomingProfile {
  /** True when a character is already stored under the file's own id. */
  readonly replaces: boolean;
  /** The name of the character being replaced, or the incoming one when nothing is. */
  readonly name: string;
}

export interface ProfileSession {
  name(): string;
  /** The whole profile, for the export that writes it to a file. */
  current(): SaveProfile;
  /** False when nothing here survives a reload, so a screen can say so before an hour is spent. */
  durable(): boolean;
  /** Renames the character and writes it. Does not wait: a dice press is not a transaction. */
  rename(name: string): void;
  /**
   * Replaces the character and writes it. Does not wait, for the reason `rename` does not.
   *
   * The general form, and `rename` is the special case of it that existed first. Experience earned
   * in a raid and a point spent on the upgrade screen both come through here, and both are things
   * a player watches happen on screen rather than transactions they wait on.
   */
  update(next: SaveProfile): void;
  /** The most recent failure, load or write, or null when everything has landed. */
  lastFailure(): SaveFailure | null;
  /**
   * What an imported profile would land on, asked before the player is asked to confirm.
   *
   * A read rather than a guess: the file carries a profileId, and whether that id is already a
   * character here is the difference between "this replaces Soggy Walrus" and "this adds a
   * character". A store that will not answer is reported as replacing nothing, because the confirm
   * still has to say something and the write that follows is the one that can fail loudly.
   */
  describeIncoming(profile: SaveProfile): Promise<IncomingProfile>;
  /**
   * Writes an imported profile and plays as it.
   *
   * Awaited, unlike `rename`. An import is a transaction the player is watching, and a screen that
   * says "imported" before the write lands is a screen that lies about the one thing this feature
   * exists for. Nothing else is deleted: the write goes to the id in the file, so a character that
   * was not overwritten is still there.
   */
  adopt(profile: SaveProfile): Promise<SaveResult<SaveProfile>>;
  /**
   * Resolves once the write in flight has landed, or immediately when none is.
   *
   * Nothing in the game waits on this. It exists so a test can assert on what a write did without
   * counting microtasks, and so a future "saving..." indicator has something to hang off.
   */
  settled(): Promise<void>;
}

export function createProfileSession(
  store: ProfileStore | null,
  profile: SaveProfile,
  initialFailure: SaveFailure | null = null,
  durable = store !== null,
): ProfileSession {
  let current = profile;
  let failure = initialFailure;
  let pending: Promise<void> = Promise.resolve();
  // Which write is the newest. Two writes close together are two stores in flight, and the store
  // hands back the record it wrote: an older one landing last would put the character back to what
  // it was a moment ago, on screen and in the next write built from it. Only the newest write's
  // result is allowed to be the answer. The failure is not guarded the same way, because a write
  // that did not land is worth saying so about whichever one it was.
  let era = 0;

  // One write path, so a rename and a level-up cannot drift on what happens when the disk refuses.
  const write = (next: SaveProfile): void => {
    current = next;
    if (store === null) return;

    era += 1;
    const mine = era;
    pending = store.save(current).then(
      (result) => {
        failure = result.ok ? null : result.failure;
        if (result.ok && mine === era) current = result.value;
      },
      (): void => {
        failure = { reason: 'storageFailed', operation: 'save', detail: 'the write threw' };
      },
    );
  };

  return {
    name: (): string => current.name,

    current: (): SaveProfile => current,

    durable: (): boolean => durable,

    rename(name: string): void {
      write({ ...current, name });
    },

    update(next: SaveProfile): void {
      write(next);
    },

    async describeIncoming(incoming: SaveProfile): Promise<IncomingProfile> {
      const stored = await store?.load(incoming.profileId);
      if (!stored?.ok) return { replaces: false, name: incoming.name };
      return { replaces: true, name: stored.value.name };
    },

    async adopt(incoming: SaveProfile): Promise<SaveResult<SaveProfile>> {
      if (store === null) {
        const unavailable: SaveFailure = {
          reason: 'storageUnavailable',
          detail: 'this browser exposes no storage this tab can keep a character in',
        };
        failure = unavailable;
        return refused(unavailable);
      }

      const written = await store.save(incoming);
      failure = written.ok ? null : written.failure;
      if (written.ok) current = written.value;
      return written;
    },

    lastFailure: (): SaveFailure | null => failure,

    settled: (): Promise<void> => pending,
  };
}

/**
 * Storage, then the character, then a session over both.
 *
 * A profile that will not load is not a reason to refuse to start the game. A fresh character is
 * created instead, and the failure that caused it is kept: nothing is deleted, so the record that
 * would not read is still there for the export path (#23) and for a character picker (#28).
 */
export async function bootProfileSession(
  options: ProfileStoreOptions = {},
): Promise<ProfileSession> {
  const storage = await openSaveStorage(options);
  const active = await resolveActiveProfile(storage.store);

  if (active.ok) {
    return createProfileSession(storage.store, active.value, storage.problem, storage.durable);
  }

  const created = await storage.store.create(rollPlayerName());
  if (created.ok) {
    return createProfileSession(storage.store, created.value, active.failure, storage.durable);
  }

  // Storage refused twice. The game still runs, under a rolled name, saving nothing.
  return createProfileSession(null, newProfile('unsaved', rollPlayerName(), 0), created.failure);
}
